import { Archive, BellPlus, LucideCheck, LucideCheckCircle, LucideCircle, LucideEllipsisVertical, LucideListChecks, LucideListTodo, LucidePlus, LucideTrash2, LucideUserPlus, LucideUsers, LucideX, Pin, PinOff } from "lucide-react";
import BackButtonLayout from "../../../layouts/BackButtonLayout";
import { Input } from "../../../components/generic/Input";
import { ApiHook, useApi } from "../../../hooks/api.hook";
import { useNavigate, useParams } from "react-router";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import LoadingPage from "../../../components/generic/LoadingPage";
import useForm from "../../../hooks/form.hook";
import { AuthContext } from "../../../contexts/auth.context";
import { cn } from "../../../util/base.util";
import { INote } from "../../../shared/type/list.type";
import { NoteType } from "../../../shared/enum/note.enum";
import List, { ListItem } from "./components/List";
import { useBoolState } from "../../../hooks/bool-state.hook";
import { ToasterContext } from "../../../contexts/toaster.context";
import UsersModal from "./components/UsersModal";
import ConfirmationModal from "../../../components/generic/ConfirmationModal";
import { SocketContext } from "../../../contexts/socket.context";

const NoteCtx = createContext<{
    reload: () => void;
    service?: ApiHook<INote>;
}>({
    reload: () => {}
});

export const useNoteCtx = () => {
    const ctx = useContext(NoteCtx);
    if (!ctx) throw new Error("No note context found");
    return ctx;
}

export default function NotePage() {
    const params = useParams<{id: string;}>();
    const noteService = useApi<INote>(`list/${params.id}`);
    const auth = useContext(AuthContext);
    const socket = useContext(SocketContext);

    const bodyRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const bUserModal = useBoolState();
    const bDeleteNote = useBoolState();

    const [note, setNote] = useState<INote|undefined>(undefined);
    const [saveTimer, setSaveTimer] = useState(-1);
    

    const noteForm = useForm({
        label: "",
        body: ""
    });

    const listForm = useForm({
        label: ""
    });
    const bAddListItem = useBoolState();

    const bDeleteAllItems = useBoolState();
    const bDeleteCheckedItems = useBoolState();

    const toast = useContext(ToasterContext);
    const nav = useNavigate();

    const [scroll, setScroll] = useState(0);
    const scrollRef = useRef<HTMLDivElement>(null);

    const handleCheckAll = useCallback(async () => {
        if (!note) return;

        const res = await noteService.put("checkAll", {state: true});

        if (res.ok) {
            setNote({...note, items: note.items.map(v => ({...v, isChecked: true}))});
        }
    }, [note]);
    const handleUncheckAll = useCallback(async () => {
        if (!note) return;
        const res = await noteService.put("checkAll", {state: false});

        if (res.ok) {
            setNote({...note, items: note.items.map(v => ({...v, isChecked: false}))});
        }
    }, [note]);
    const handleDeleteChecked = useCallback(async () => {
        if (!note) return;

        const res = await noteService.delete("items");

        if (res.ok && !!res.data) {
            setNote({...note, items: res.data.items});
            bDeleteCheckedItems.disable();
        }
    }, [note]);
    const handleDeleteAll = useCallback(async () => {
        if (!note) return;

        const res = await noteService.delete("items", {all: true});

        if (res.ok && !!res.data) {
            setNote({...note, items: res.data.items});
            bDeleteAllItems.disable();
        }
    }, [note]);

    const deleteNote = useCallback(async () => {
        if (!note) return;

        const res = await noteService.delete();

        if (res.ok) {
            nav("/");
        }
    }, [note]);

    const addListItem = useCallback(() => {
        if (!note) return;
        listForm.submit(`/list/${note.id}`, {
            onSuccess: (data) => {
                listForm.set({label: ""});
                noteService.get();
            }
        });
    }, [note, listForm]);

    const scrollTo = useCallback(() => {
        if (!scrollRef.current) return;
        const div = scrollRef.current;

        div.scrollTo({top: scroll});
    }, [scroll, scrollRef.current]);

    const checkItem = useCallback((id: number) => {
        if (!note) return;

        const clone = JSON.parse(JSON.stringify(note)) as INote;
        let stateToSend: boolean|undefined;
        for (const item of clone.items) {
            if (item.id === id) {
                stateToSend = item.isChecked = !item.isChecked;
                break;
            }
        }

        setNote(clone);

        noteService.patch("", {
            checks: [{
                id,
                state: stateToSend
            }]
        });

        // scrollTo();
    }, [note]);

    function SaveNote() {
        if (!note) return;

        noteForm.submit(`list/${note.id}`, {
            method: "PATCH"
        });
    }

    const PinNote = useCallback(() => {
        if (!note || !auth.user) return;

        const pinState = auth.user.pinnedNotes?.map(v => v.id).includes(note.id) ? false : true;

        noteForm.submit(`list/${note.id}`, {
            method: "PATCH",
            body: {
                pinned: pinState
            }
        });

        if (!auth.user.pinnedNotes) return;
        const map = auth.user.pinnedNotes.map(v => v.id);
        if (map.includes(note.id)) {
            //remove
            auth.UpdateUserProperties({
                pinnedNotes: auth.user.pinnedNotes.filter(v => v.id !== note.id)
            });
            return;
        }

        auth.UpdateUserProperties({
            pinnedNotes: [...auth.user.pinnedNotes, note]
        });
    }, [note, auth.user]);

    // function TrashNote() {
    //     if (!note) return;

    //     noteForm.submit(`list/${note.id}`, {
    //         method: "PATCH",
    //         body: {
    //             trashed: true
    //         }
    //     });
    // }

    // function ArchiveNote() {
    //     if (!note) return;

    //     noteForm.submit(`list/${note.id}`, {
    //         method: "PATCH",
    //         body: {
    //             archived: true
    //         }
    //     });
    // }

    const isNoteOwner = useMemo(() => {
        if (!note || !auth.user) return;

        return note.ownerId === auth.user.id;
    }, [note, auth.user]);

    useEffect(() => {
        if (!noteService.data) return;
        setNote(noteService.data);
        noteForm.set({label: noteService.data.label || undefined, body: noteService.data.body || ""});
    }, [noteService.data]);

    useEffect(() => {
        if (saveTimer === -1) return;
        if (saveTimer === 0) {
            SaveNote();
            return;
        }

        const timer = setTimeout(() => {
            setSaveTimer(prev => prev - 1);
        }, 1000);

        return () => {
            clearTimeout(timer);
        };
    }, [saveTimer]);

    useEffect(() => {
        noteForm.set({body: bodyRef.current?.innerText || ""});
    }, [bodyRef.current?.innerText]);

    // useEffect(() => {
    //     if (!contentRef.current) return;
    //     const div = contentRef.current;
    //     const disableDefaults = (e: Event) => {
    //         e.preventDefault();
    //     }

    //     div.addEventListener("mousedown", disableDefaults);
        
    //     return () => {
    //         div.removeEventListener("mousedown", disableDefaults);
    //     }
    // }, [contentRef.current]);

    useEffect(() => {
        noteService.get()
            .then(res => {
                if (!res.ok) {
                    window.location.href = "/";
                }
            })

        const handleKeyDown = (e: KeyboardEvent) => {
            setSaveTimer(2);
        }

        

        window.addEventListener("keydown", handleKeyDown);
        

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
            
        };

    }, []);

    useEffect(() => {
        if (!note) return;
        scrollTo();
    }, [note, scrollTo]);

    

    useEffect(() => {
        if (!scrollRef.current) return;
        const div = scrollRef.current;
        const handleScroll = () => {
            setScroll(div.scrollTop);
        }

        div.addEventListener("scroll", handleScroll);

        return () => {
            div.removeEventListener("scroll", handleScroll);
        }
    }, [scrollRef.current]);

    useEffect(() => {
        //join note socket group
        if (!note || !auth.user) return;
        const usr = auth.user;
        socket.join(`list-${note.id}`);

        socket.on("list-update", (res: {
            note: INote;
            sender: number;
        }) => {
            if (res.sender !== usr.id) {
                //replace note
                setNote(res.note);
                noteForm.set({label: res.note.label || undefined});
            }
        });

        socket.on("list-delete", (res: {
            sender: number
        }) => {
            if (res.sender !== usr.id) {
                toast.makeToast("The owner has deleted this note.");
                nav("/");
            }
        });

        return () => {
            socket.leave(`list-${note.id}`);
            socket.off("list-update");
            socket.off("list-delete");
        }
    }, [note?.id, auth.user]);

    if (!note) {
        return <LoadingPage />;
    }

    //note
    return (
        <BackButtonLayout
        className={'border-b border-[#ffffff33]'}
        right={
            <div className="flex items-center gap-2 pr-2">
                <div><button onClick={PinNote} className="btn btn-ghost btn-circle">{auth.user?.pinnedNotes?.map(v => v.id).includes(note.id) ? <PinOff /> : <Pin />}</button></div>
                <div><button onClick={bUserModal.enable} className="btn btn-ghost btn-circle">{isNoteOwner ? <LucideUserPlus /> : <LucideUsers />}</button></div>
            </div>
        }
        >
            <div ref={scrollRef} className="p-4 mx-auto max-w-screen-md h-full overflow-auto">
                <div className="flex items-center gap-2">
                    <div className="flex-1">
                        <input type="text" value={noteForm.state.label} onChange={e => noteForm.set({label: e.target.value})} placeholder="Title" className="text-xl w-full outline-none py-4 bg-transparent w-full" />
                    </div>
                    <div>
                        <div className="dropdown dropdown-end">
                            <button className="btn btn-ghost btn-circle"><LucideEllipsisVertical /></button>
                            <div tabIndex={0} className="dropdown-content">
                                <ul className="w-48 bg-base-100 p-2 menu">
                                    {note.type === 1 && (
                                        <>
                                        <li><span onClick={handleCheckAll}><LucideCheckCircle size={16} /> Check all</span></li>
                                        <li><span onClick={handleUncheckAll}><LucideCircle size={16} /> Uncheck all</span></li>
                                        <div className="divider m-0"></div>
                                        <li><span onClick={bDeleteCheckedItems.enable}><LucideListChecks size={16} /> Delete checked</span></li>
                                        <li><span onClick={bDeleteAllItems.enable}><LucideListTodo size={16} /> Delete all</span></li>
                                        </>
                                    )}
                                    {isNoteOwner && (
                                        <>
                                        {note.type === 1 && (
                                            <div className="divider m-0"></div>
                                        )}
                                        <li><span onClick={bDeleteNote.enable}><LucideTrash2 size={16} /> Delete {note.type === 1 ? "list" : "note"}</span></li>
                                        </>
                                    )}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                <div ref={contentRef}>
                    {note.type === NoteType.List && (
                        // <List note={note} />
                        <>
                        {note.items.filter(v => !v.isChecked).map((item, index) => (
                            <ListItem key={`note-${note.id}-item-${item.id}`} item={item} check={checkItem} />
                            // <div key={`note-${note.id}-item-${item.id}`} onClick={() => checkItem(item.id)} className="flex items-center gap-2 p-2">
                            //     <div><input type="checkbox" defaultChecked={item.isChecked} className="checkbox checkbox-sm" /></div>
                            //     <div className="flex-1 cursor-pointer">
                            //         <p className={cn(
                            //             item.isChecked && 'line-through opacity-50'
                            //         )}>{item.label}</p>
                            //     </div>
                            // </div>
                        ))}

                        {bAddListItem.value && (
                            <div className="flex items-center gap-2 p-2">
                                <div><input readOnly checked={false} type="checkbox" className="checkbox checkbox-sm" /></div>
                                <div className="flex-1 flex items-center gap-2">
                                    <div className="flex-1">
                                        <input value={listForm.state.label} onChange={e => {
                                            listForm.set({label: e.target.value});
                                        }}
                                        onKeyDown={e => {
                                            // console.log(e.key, e.code)
                                            if (e.key === "Enter") {
                                                addListItem();
                                            }
                                        }} autoFocus type="text" className="input input-sm w-full" />
                                    </div>
                                    <div>
                                        <button onClick={() => {
                                            addListItem();
                                            bAddListItem.disable();
                                        }} className="btn btn-sm btn-circle btn-ghost"><LucideCheck /></button>
                                    </div>
                                    <div>
                                        <button onClick={() => {
                                            listForm.set({label: ""});
                                            bAddListItem.disable();
                                        }} className="btn btn-sm btn-circle btn-ghost"><LucideX /></button>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div onClick={bAddListItem.enable} className="flex items-center gap-2 p-2 cursor-pointer">
                            <div><LucidePlus /></div>
                            <div className="flex-1">
                                List item
                            </div>
                        </div>

                        {!!note.items.filter(v => v.isChecked).length && (
                            <div className="collapse collapse-arrow">
                                <input type="checkbox" defaultChecked />
                                <div className="collapse-title after:start-5 after:end-auto pe-4 ps-12 text-white/50">{note.items.filter(v => v.isChecked).length} checked item(s)</div>
                                <div className="collapse-content">
                                    {note.items.filter(v => v.isChecked).map((item, index) => (
                                        <ListItem key={`note-${note.id}-item-${item.id}`} item={item} check={checkItem} />
                                        // <div key={`note-${note.id}-item-${item.id}`} onClick={() => checkItem(item.id)} className="flex items-center gap-2 p-2">
                                        //     <div><input type="checkbox" defaultChecked={item.isChecked} className="checkbox checkbox-sm" /></div>
                                        //     <div className="flex-1 cursor-pointer">
                                        //         <p className={cn(
                                        //             item.isChecked && 'line-through opacity-50'
                                        //         )}>{item.label}</p>
                                        //     </div>
                                        // </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        </>
                    )}
                </div>

                {note.type === NoteType.Note && (
                    <div className="relative text-sm">
                        <div ref={bodyRef} contentEditable suppressContentEditableWarning={true} className="min-h-64 outline-none whitespace-pre-wrap">{note.body}</div>
                        {/^\s*$/.test(noteForm.state.body) && (
                            <div className="absolute top-0 left-0 italic opacity-50 pointer-events-none">Note</div>
                        )}
                    </div>
                )}
            </div>

            {bUserModal.value && (
                <NoteCtx.Provider value={{
                    reload: noteService.get,
                    service: noteService
                }}>
                    <UsersModal note={note} onClose={bUserModal.disable} />
                </NoteCtx.Provider>
            )}

            {bDeleteNote.value && (
                <ConfirmationModal dangerous okText="Delete" onCancel={bDeleteNote.disable}
                onOk={deleteNote}>
                    <p className="px-2">Delete the {note.type === 1 ? "list" : "note"}? This cannot be undone.</p>
                </ConfirmationModal>
            )}

            {bDeleteCheckedItems.value && (
                <ConfirmationModal dangerous okText="Delete" onCancel={bDeleteCheckedItems.disable}
                onOk={handleDeleteChecked}>
                    <p className="px-2">Delete all checked items? This cannot be undone.</p>
                </ConfirmationModal>
            )}

            {bDeleteAllItems.value && (
                <ConfirmationModal dangerous okText="Delete" onCancel={bDeleteAllItems.disable}
                onOk={handleDeleteAll}>
                    <p className="px-2">Delete all items? This cannot be undone.</p>
                </ConfirmationModal>
            )}
        </BackButtonLayout>
    );
}